import React from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  IconButton,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Construction as CementIcon,
  Landscape as AggregateIcon,
} from '@mui/icons-material';

import { commonButtonStyles } from '../../../../components/styles/common-style';

import type { RawMaterials, RawMaterial } from '../../types/company.types';

import { getStylesForEditableField } from '@/utils/helpers';
import { PaperBoxStyles } from '@/components/styles';

// Cement type options
const CEMENT_TYPES = [
  { value: '1', label: '普通セメント(N)' },
  { value: '2', label: '高炉セメントB種(BB)' },
  { value: '3', label: '早強セメント(H)' },
  { value: '4', label: '中庸熱セメント(M)' },
  { value: '5', label: '低熱セメント(L)' },
  { value: 'other', label: 'その他' },
];

type RawMaterialCategory = 'cement' | 'coarseAggregate' | 'fineAggregate';

interface RawMaterialsSectionProps {
  rawMaterials: RawMaterials;
  originalRawMaterials?: RawMaterials;
  isEditMode?: boolean;
  onAddRawMaterial: (category: RawMaterialCategory) => void;
  onRemoveRawMaterial: (category: RawMaterialCategory, index: number) => void;
  onUpdateRawMaterial: (
    category: RawMaterialCategory,
    index: number,
    field: keyof RawMaterial,
    value: string | boolean
  ) => void;
  getFieldError: (field: string) => string | undefined;
  isFieldValid: (field: string) => boolean;
}

export const RawMaterialsSection: React.FC<RawMaterialsSectionProps> = ({
  rawMaterials,
  originalRawMaterials,
  isEditMode,
  onAddRawMaterial,
  onRemoveRawMaterial,
  onUpdateRawMaterial,
  getFieldError,
  isFieldValid,
}) => {
  const [activeTab, setActiveTab] = React.useState(0);

  const materialCategories = [
    {
      key: 'cement' as const,
      label: 'セメント',
      icon: <CementIcon />,
      materials: rawMaterials.cement,
      originalMaterials: originalRawMaterials?.cement,
    },
    {
      key: 'coarseAggregate' as const,
      label: '粗骨材',
      icon: <AggregateIcon />,
      materials: rawMaterials.coarseAggregate,
      originalMaterials: originalRawMaterials?.coarseAggregate,
    },
    {
      key: 'fineAggregate' as const,
      label: '細骨材',
      icon: <AggregateIcon />,
      materials: rawMaterials.fineAggregate,
      originalMaterials: originalRawMaterials?.fineAggregate,
    },
  ];

  const currentCategory = materialCategories[activeTab];

  const renderMaterialList = (category: typeof currentCategory) => {
    return (
      <Box>
        {category.materials.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: 'text.secondary',
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {category.icon &&
              React.cloneElement(category.icon, { sx: { fontSize: 48, mb: 1, color: '#0047BA' } })}
            <Typography variant='body1'>{category.label}</Typography>
            <Button
              variant='outlined'
              startIcon={<AddIcon />}
              onClick={() => onAddRawMaterial(category.key)}
              disabled={category.materials.length >= 10}
              sx={{
                ...commonButtonStyles,
                mt: 2,
                px: 3,
                py: 1,
              }}
            >
              入力行を追加
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {category.materials.map((material, index) => {
              const originalMaterialItem =
                material.index !== undefined
                  ? category.originalMaterials?.[material.index]
                  : undefined;
              return (
                <Paper key={material.id} variant='outlined' sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 3,
                    }}
                  >
                    <Typography variant='subtitle1' fontWeight={600}>
                      {category.label}
                    </Typography>
                    <IconButton
                      color='error'
                      onClick={() => onRemoveRawMaterial(category.key, index)}
                      size='small'
                      sx={{ color: '#FF8985' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  {category.key === 'cement' ? (
                    // Cement form with dropdown
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        {/* Cement Type Dropdown */}
                        <FormControl
                          sx={{ minWidth: 200 }}
                          error={!isFieldValid(`rawMaterials.${category.key}.${index}.type`)}
                        >
                          <InputLabel>種類</InputLabel>
                          <Select
                            value={material.type}
                            onChange={(e) =>
                              onUpdateRawMaterial(category.key, index, 'type', e.target.value)
                            }
                            label='種類'
                            slotProps={{
                              input: {
                                sx: getStylesForEditableField(
                                  !!isEditMode,
                                  originalMaterialItem?.type,
                                  material.type
                                ),
                              },
                            }}
                          >
                            {CEMENT_TYPES.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Other Type Field (conditional) */}
                        {material.type === 'other' && (
                          <TextField
                            label='その他セメント種類を入力'
                            value={material.otherType || ''}
                            onChange={(e) =>
                              onUpdateRawMaterial(category.key, index, 'otherType', e.target.value)
                            }
                            error={!isFieldValid(`rawMaterials.${category.key}.${index}.otherType`)}
                            helperText={getFieldError(
                              `rawMaterials.${category.key}.${index}.otherType`
                            )}
                            sx={{ minWidth: 250 }}
                            slotProps={{
                              input: {
                                inputProps: {
                                  maxLength: 50,
                                },
                                sx: getStylesForEditableField(
                                  !!isEditMode,
                                  originalMaterialItem?.type,
                                  material.type
                                ),
                              },
                            }}
                          />
                        )}

                        {/* Manufacturer */}
                        <TextField
                          label='メーカー'
                          placeholder='メーカーを入力'
                          value={material.manufacturerOrOrigin}
                          autoComplete='off'
                          onChange={(e) =>
                            onUpdateRawMaterial(
                              category.key,
                              index,
                              'manufacturerOrOrigin',
                              e.target.value
                            )
                          }
                          error={
                            !isFieldValid(
                              `rawMaterials.${category.key}.${index}.manufacturerOrOrigin`
                            )
                          }
                          helperText={getFieldError(
                            `rawMaterials.${category.key}.${index}.manufacturerOrOrigin`
                          )}
                          sx={{ minWidth: 300 }}
                          slotProps={{
                            input: {
                              inputProps: {
                                maxLength: 50,
                              },
                              sx: getStylesForEditableField(
                                !!isEditMode,
                                originalMaterialItem?.manufacturerOrOrigin,
                                material.manufacturerOrOrigin
                              ),
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  ) : (
                    // Aggregate form (coarse/fine)
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        {/* Type */}
                        <TextField
                          label='種類'
                          placeholder='種類を入力'
                          value={material.type}
                          autoComplete='off'
                          onChange={(e) =>
                            onUpdateRawMaterial(category.key, index, 'type', e.target.value)
                          }
                          error={!isFieldValid(`rawMaterials.${category.key}.${index}.type`)}
                          helperText={getFieldError(`rawMaterials.${category.key}.${index}.type`)}
                          sx={{ minWidth: 200 }}
                          slotProps={{
                            input: {
                              inputProps: {
                                maxLength: 50,
                              },
                              sx: getStylesForEditableField(
                                !!isEditMode,
                                originalMaterialItem?.type,
                                material.type
                              ),
                            },
                          }}
                        />

                        {/* Origin */}
                        <TextField
                          label='産地'
                          placeholder='産地を入力'
                          value={material.manufacturerOrOrigin}
                          autoComplete='off'
                          onChange={(e) =>
                            onUpdateRawMaterial(
                              category.key,
                              index,
                              'manufacturerOrOrigin',
                              e.target.value
                            )
                          }
                          error={
                            !isFieldValid(
                              `rawMaterials.${category.key}.${index}.manufacturerOrOrigin`
                            )
                          }
                          helperText={getFieldError(
                            `rawMaterials.${category.key}.${index}.manufacturerOrOrigin`
                          )}
                          sx={{ minWidth: 200 }}
                          slotProps={{
                            input: {
                              inputProps: {
                                maxLength: 50,
                              },
                              sx: getStylesForEditableField(
                                !!isEditMode,
                                originalMaterialItem?.manufacturerOrOrigin,
                                material.manufacturerOrOrigin
                              ),
                            },
                          }}
                        />
                      </Box>

                      {/* Limestone Radio Buttons */}
                      <FormControl component='fieldset'>
                        <FormLabel component='legend'>石灰石</FormLabel>
                        <RadioGroup
                          row
                          value={material.hasLimestone ? 'yes' : 'no'}
                          onChange={(e) =>
                            onUpdateRawMaterial(
                              category.key,
                              index,
                              'hasLimestone',
                              e.target.value === 'yes'
                            )
                          }
                          sx={{
                            width: 'fit-content',
                            ...getStylesForEditableField(
                              !!isEditMode,
                              originalMaterialItem?.hasLimestone?.toString(),
                              material.hasLimestone?.toString()
                            ),
                          }}
                        >
                          <FormControlLabel value='yes' control={<Radio />} label='あり' />
                          <FormControlLabel value='no' control={<Radio />} label='なし' />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Box>
        )}

        {/* Add Raw Material Button (bottom) */}
        {category.materials.length > 0 && category.materials.length < 10 && (
          <Box sx={{ textAlign: 'center', mt: 3, mb: 4 }}>
            <Button
              variant='outlined'
              startIcon={<AddIcon />}
              onClick={() => onAddRawMaterial(category.key)}
              sx={{
                ...commonButtonStyles,
                px: 3,
                py: 1,
              }}
            >
              入力行を追加 ({category.materials.length}/10)
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <PaperBoxStyles elevation={1} sx={{ mb: 2, paddingBottom: '40px' }}>
      <Typography
        variant='h5'
        fontWeight='bold'
        sx={{
          mb: 3,
          color: '#1A1C20',
          fontSize: '22px',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        原材料
      </Typography>

      {/* Material Category Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          aria-label='raw materials tabs'
          sx={{
            '& .MuiTab-root.Mui-selected': {
              color: '#0047BA',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#0047BA',
            },
          }}
        >
          {materialCategories.map((category, index) => (
            <Tab
              key={category.key}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {category.icon}
                  {category.label}
                  {category.materials.length > 0 && (
                    <Box
                      sx={{
                        backgroundColor: '#0047BA',
                        color: 'white',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        lineHeight: '20px',
                        boxSizing: 'border-box',
                      }}
                    >
                      {category.materials.length}
                    </Box>
                  )}
                </Box>
              }
              id={`raw-materials-tab-${index}`}
              aria-controls={`raw-materials-tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Box>

      {/* Material Content */}
      {renderMaterialList(currentCategory)}
    </PaperBoxStyles>
  );
};
